import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { sendPasswordReset } from '@/lib/api/auth';

const schema = z.object({
  email: z.string().email('Enter a valid email'),
});
type FormValues = z.infer<typeof schema>;

export function ForgotPassword() {
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = async (values: FormValues) => {
    setSubmitError(null);
    try {
      await sendPasswordReset(values.email);
      setSent(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Could not send reset link.');
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-4 py-12">
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="mb-8 flex items-center justify-center gap-3 text-navy"
        >
          <img src="/brand/cpn_mark.svg" alt="" aria-hidden="true" className="h-10 w-10" />
          <span className="font-display text-base font-semibold">
            The Cambodian Professionals Network
          </span>
        </Link>

        <Card>
          {!sent ? (
            <>
              <CardHeader>
                <CardTitle>Reset your password</CardTitle>
                <CardDescription>
                  Enter your email and we'll send a reset link.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      aria-invalid={!!errors.email}
                      {...register('email')}
                    />
                    {errors.email && (
                      <p role="alert" aria-live="polite" className="text-xs text-destructive">
                        {errors.email.message}
                      </p>
                    )}
                  </div>
                  {submitError && (
                    <p role="alert" aria-live="polite"
                      className="rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">
                      {submitError}
                    </p>
                  )}
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending…' : 'Send reset link'}
                  </Button>
                </form>
                <p className="mt-6 text-center text-sm text-muted">
                  <Link to="/signin" className="font-medium text-gold-dark hover:underline">
                    Back to sign in
                  </Link>
                </p>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gold/15 text-gold-dark">
                  <Mail className="h-5 w-5" aria-hidden="true" />
                </div>
                <CardTitle className="mt-4">Check your email</CardTitle>
                <CardDescription>
                  We sent a password reset link to{' '}
                  <strong className="text-navy">{getValues('email')}</strong>.
                  Click it to choose a new password.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted">
                  Didn't receive it? Check spam, or{' '}
                  <button
                    type="button"
                    onClick={() => setSent(false)}
                    className="font-medium text-gold-dark hover:underline"
                  >
                    try again
                  </button>
                  .
                </p>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
